import { Flex, Heading, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useState } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { TableSkeletonRows } from '../../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { useAppToast, useHasRole } from '../../../../helpers'
import { IWebsiteFont } from '../../../../interfaces/Website/Banners/IWebsiteFont'
import WebsiteFontService from '../../../../services/Website/WebsiteFontService'
import { fontFamilyOf } from '../bannerFontCss'
import { useBannerFonts } from '../useBannerFonts'
import WebsiteFontFormModal from './WebsiteFontFormModal'

// Texto neutro, com maiúsculas, minúsculas, acentos e números: é o que mostra se a fonte tem
// os caracteres do português antes de ela ir para um banner.
const PREVIEW_TEXT = 'Lorem ipsum dolor sit amet — ÁÉÍÓÚ ãõç 0123456789'

/**
 * Fontes enviadas para o editor dos Banners Home. Entram no seletor de fonte de cada trecho ao
 * lado das três que o site já carrega, e o site só baixa as que algum banner ativo usa.
 */
export default function WebsiteFontsPage({ bannerFonts }: { bannerFonts: ReturnType<typeof useBannerFonts> }) {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const canEdit = hasRole('edit_banner')
  const { uploaded, isLoaded, loadError, reload } = bannerFonts
  const formModal = useDisclosure()
  const deleteDialog = useDisclosure()
  const [fontToEdit, setFontToEdit] = useState<IWebsiteFont>()
  const [fontToDelete, setFontToDelete] = useState<IWebsiteFont>()
  const columns = canEdit ? 5 : 4

  const openForm = (font?: IWebsiteFont) => {
    setFontToEdit(font)
    formModal.onOpen()
  }

  const handleDelete = async () => {
    if (!fontToDelete) return

    try {
      await new WebsiteFontService().delete(fontToDelete.id)
      toast.success('Fonte excluída')
      reload()
    } catch (error) {
      // 422 é a fonte em uso: a mensagem da API lista os banners que precisam trocar de fonte.
      toast.error('Não foi possível excluir a fonte', error)
    } finally {
      deleteDialog.onClose()
    }
  }

  return (
    <>
      <Flex justify="space-between" align="center" mb={2}>
        <Heading as="h2">Fontes</Heading>
        {canEdit && <ActionButton action="upload" label="Enviar fonte" mr={0} onClick={() => openForm()} />}
      </Flex>
      <Text variant="caption" mb={6}>
        Montserrat, Roboto e Georama já estão disponíveis no editor dos banners. Envie aqui outras fontes para usar nos
        títulos, subtítulos e botões — só as fontes com licença para uso na web.
      </Text>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Fonte</Th>
              <Th>Prévia</Th>
              <Th>Formato</Th>
              <Th isNumeric>Ajuste</Th>
              {canEdit && <Th w="1%">Ações</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {!isLoaded ? (
              <TableSkeletonRows cols={columns} rows={3} />
            ) : loadError ? (
              <TableErrorRow colSpan={columns} entity="fontes" feminine onRetry={reload} />
            ) : uploaded.length === 0 ? (
              <TableEmptyRow colSpan={columns} entity="fonte" feminine />
            ) : (
              uploaded.map((font) => (
                <Tr key={font.id}>
                  <Td>{font.name}</Td>
                  <Td whiteSpace="normal" fontSize="lg" fontFamily={`'${fontFamilyOf(font)}', sans-serif`}>
                    {PREVIEW_TEXT}
                  </Td>
                  <Td textTransform="uppercase">{font.format}</Td>
                  <Td isNumeric>{font.size_adjust}%</Td>
                  {canEdit && (
                    <Td whiteSpace="nowrap">
                      <ActionButton action="edit" onClick={() => openForm(font)} />
                      <ActionButton
                        action="delete"
                        mr={0}
                        onClick={() => {
                          setFontToDelete(font)
                          deleteDialog.onOpen()
                        }}
                      />
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <WebsiteFontFormModal isOpen={formModal.isOpen} onClose={formModal.onClose} onSaved={reload} font={fontToEdit} />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        onConfirm={handleDelete}
        action="delete"
        entity="fonte"
        feminine
        name={fontToDelete?.name}
        warning="Fontes em uso por algum banner não podem ser excluídas."
      />
    </>
  )
}
